import { ScanErrorKind, type Token, TokenKind } from '../../json/tokens';
import type { DiagnosticCode } from '../../lint/codes';
import { isDigit, isHex, scanStructure, structuralTokens, textOf } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Rewrite a number literal that every other language accepts and JSON does not.
 *
 * Five forms, each one a spelling change with the value preserved exactly:
 *   `0x1F` → `31`, `+1` → `1`, `.5` → `0.5`, `1.` → `1`, `1e` → `1`, `01` → `1`.
 *
 * None of them existed in the legacy pipeline (recon B22–B24: `no-repair`), and the value is
 * preserved rather than re-serialised, which is why `1e10`, `-0` and a 20-digit id that a
 * `double` cannot hold are not touched by this rule at all — a number that is already legal
 * JSON matches no branch here (ADR-0013).
 *
 * `jsonc-parser` splits these literals in revealing ways: `0x1F` arrives as `Number('0')`
 * followed by `Unknown('x1F')`, `01` as two `Number` tokens, `1.` as one `Number` carrying
 * `ScanErrorKind.UnterminatedNumber`. Adjacency (`a.to === b.from`) is what tells a split
 * literal apart from two values that are genuinely missing a comma, and it is a fact about
 * tokens rather than a guess about spacing.
 */
export const fixNumberLiteralRule: RepairRule = {
  id: 'fix-number-literal',
  confidence: 'safe',
  priority: 37,
  resolves: [
    'number/hex-literal',
    'number/leading-plus',
    'number/leading-decimal-point',
    'number/trailing-decimal-point',
    'number/incomplete-exponent',
    'number/leading-zero',
    'number/invalid-literal',
  ],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const { namedKeys } = scanStructure(ctx.source, ctx.tokens);
    const sig = structuralTokens(ctx.source, ctx.tokens);
    const proposals: RepairProposal[] = [];
    let consumedTo = -1;

    for (let i = 0; i < sig.length; i += 1) {
      const token = sig[i];
      if (token === undefined || token.from < consumedTo) continue;
      // Only a token the grammar reads as a key AND that is followed by a colon is
      // provably a property name; that is the one quote-unquoted-key claims. Anything
      // looser would skip the `Infinity` of `-Infinity`, which recovery also parks in key
      // position.
      if (namedKeys.has(token)) continue;

      const found =
        hexLiteral(ctx, sig, i) ??
        leadingZeros(ctx, sig, i) ??
        truncatedLiteral(ctx, token) ??
        signOrPointLiteral(ctx, token);
      if (found === undefined) continue;

      consumedTo = found.to;
      proposals.push({
        ruleId: 'fix-number-literal',
        span: { from: found.from, to: found.to },
        edits: [{ from: found.from, to: found.to, insert: found.insert }],
        labelCode: 'fix.fix-number-literal',
        labelParams: { literal: ctx.source.slice(found.from, found.to), replacement: found.insert },
        resolves: found.code,
      });
    }

    return proposals;
  },
};

interface Rewrite {
  readonly from: number;
  readonly to: number;
  readonly insert: string;
  readonly code: DiagnosticCode;
}

/** `0x1F`, `-0XFF`: a `Number` of `0` glued to an `Unknown` of hex digits. */
function hexLiteral(ctx: RepairContext, sig: readonly Token[], index: number): Rewrite | undefined {
  const token = sig[index];
  const next = sig[index + 1];
  if (token === undefined || next === undefined) return undefined;
  if (token.kind !== TokenKind.Number || next.kind !== TokenKind.Unknown) return undefined;
  if (token.to !== next.from) return undefined;

  const head = textOf(ctx.source, token);
  if (head !== '0' && head !== '-0') return undefined;

  const tail = textOf(ctx.source, next);
  const marker = tail.charAt(0);
  if (marker !== 'x' && marker !== 'X') return undefined;

  const digits = tail.slice(1);
  if (!isHex(digits)) return undefined;

  const value = Number.parseInt(digits, 16);
  if (!Number.isSafeInteger(value)) return undefined;

  return {
    from: token.from,
    to: next.to,
    insert: `${head === '-0' ? '-' : ''}${value}`,
    code: 'number/hex-literal',
  };
}

/** `01`, `-007`: a run of adjacent `Number` tokens that is really one literal. */
function leadingZeros(
  ctx: RepairContext,
  sig: readonly Token[],
  index: number,
): Rewrite | undefined {
  const first = sig[index];
  if (first === undefined || first.kind !== TokenKind.Number) return undefined;
  if (first.scanError !== ScanErrorKind.None) return undefined;

  let last: Token = first;
  let text = textOf(ctx.source, first);

  for (let i = index + 1; i < sig.length; i += 1) {
    const next = sig[i];
    if (next === undefined || next.kind !== TokenKind.Number) break;
    if (next.to === next.from || last.to !== next.from) break;
    text += textOf(ctx.source, next);
    last = next;
  }

  if (last === first) return undefined;

  const negative = text.startsWith('-');
  const digits = negative ? text.slice(1) : text;
  for (const char of digits) {
    if (!isDigit(char)) return undefined;
  }

  // No regex, here or anywhere else under `rules/` — ADR-0011 is easier to audit when the
  // directory simply contains none.
  let start = 0;
  while (start + 1 < digits.length && digits.charAt(start) === '0') start += 1;

  const insert = `${negative ? '-' : ''}${digits.slice(start)}`;
  if (insert === text) return undefined;

  return { from: first.from, to: last.to, insert, code: 'number/leading-zero' };
}

/** `1.`, `1e`, `1E+`: the scanner ran out of literal. */
function truncatedLiteral(ctx: RepairContext, token: Token): Rewrite | undefined {
  if (token.kind !== TokenKind.Number) return undefined;
  if (token.scanError !== ScanErrorKind.UnterminatedNumber) return undefined;

  const text = textOf(ctx.source, token);
  const exponent = Math.max(text.indexOf('e'), text.indexOf('E'));

  if (exponent > 0) {
    const head = text.slice(0, exponent);
    if (!isNumberBody(head)) return undefined;
    return { from: token.from, to: token.to, insert: head, code: 'number/incomplete-exponent' };
  }

  if (text.endsWith('.')) {
    const head = text.slice(0, -1);
    if (!isNumberBody(head)) return undefined;
    return { from: token.from, to: token.to, insert: head, code: 'number/trailing-decimal-point' };
  }

  return undefined;
}

/** `+1` and `.5`: an `Unknown` token that is a number with one illegal character at the front. */
function signOrPointLiteral(ctx: RepairContext, token: Token): Rewrite | undefined {
  if (token.kind !== TokenKind.Unknown) return undefined;

  const text = textOf(ctx.source, token);

  if (text.startsWith('+')) {
    const body = text.slice(1);
    if (!isNumberBody(body)) return undefined;
    return { from: token.from, to: token.to, insert: body, code: 'number/leading-plus' };
  }

  if (text.startsWith('.')) {
    if (!isNumberBody(`0${text}`)) return undefined;
    return {
      from: token.from,
      to: token.to,
      insert: `0${text}`,
      code: 'number/leading-decimal-point',
    };
  }

  if (text.startsWith('-.')) {
    const insert = `-0${text.slice(1)}`;
    if (!isNumberBody(insert)) return undefined;
    return { from: token.from, to: token.to, insert, code: 'number/leading-decimal-point' };
  }

  return undefined;
}

/** `-?digits(.digits)?([eE][+-]?digits)?` — the JSON grammar, minus the leading-zero rule. */
export function isNumberBody(text: string): boolean {
  let i = 0;
  if (text.charAt(i) === '-') i += 1;

  let integral = 0;
  while (i < text.length && isDigit(text.charAt(i))) {
    i += 1;
    integral += 1;
  }
  if (integral === 0) return false;

  if (text.charAt(i) === '.') {
    i += 1;
    let fraction = 0;
    while (i < text.length && isDigit(text.charAt(i))) {
      i += 1;
      fraction += 1;
    }
    if (fraction === 0) return false;
  }

  const marker = text.charAt(i);
  if (marker === 'e' || marker === 'E') {
    i += 1;
    const sign = text.charAt(i);
    if (sign === '+' || sign === '-') i += 1;
    let exponent = 0;
    while (i < text.length && isDigit(text.charAt(i))) {
      i += 1;
      exponent += 1;
    }
    if (exponent === 0) return false;
  }

  return i === text.length;
}
